import React, { FC, useEffect } from 'react';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import Skeleton from '@material-ui/lab/Skeleton';
import WarningIcon from '@material-ui/icons/Warning';
import HomeIcon from '@material-ui/icons/Home';
import { Map, AdvancedMarker, useMap } from '@vis.gl/react-google-maps';
import { useMapAvailability } from './MapProvider';

export type LatLng = {
  lat: number;
  lng: number;
};

export type LocationMapProps = {
  position: LatLng | null;
  height?: number | string;
  draggable?: boolean;
  onPositionChange?: (position: LatLng) => void;
  emptyText?: string;
  zoom?: number;
  jobPosition?: LatLng | null;
};

const DEFAULT_EMPTY_TEXT = 'Please select an address to enable geolocation';
const UNAVAILABLE_TEXT = 'Map unavailable';
const DEFAULT_HEIGHT = 300;

export const LocationMap: FC<LocationMapProps> = ({
  position,
  height = DEFAULT_HEIGHT,
  emptyText = DEFAULT_EMPTY_TEXT,
  zoom,
  draggable = false,
  onPositionChange,
  jobPosition,
}) => {
  const classes = useStyles();
  const availability = useMapAvailability({ requireProbe: true });
  const mapId = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID;

  if (availability.status === 'unavailable') {
    return (
      <Box className={classes.unavailable} style={{ height }}>
        <WarningIcon
          fontSize="small"
          color="disabled"
          className={classes.unavailableIcon}
        />
        <Typography variant="caption" color="textSecondary" align="center">
          {UNAVAILABLE_TEXT}
        </Typography>
      </Box>
    );
  }

  if (availability.status === 'loading') {
    return (
      <Skeleton
        variant="rect"
        width="100%"
        height={height}
        className={classes.skeleton}
      />
    );
  }

  if (position === null) {
    return (
      <Box className={classes.placeholder} style={{ height }}>
        <Typography variant="body2" color="textSecondary" align="center">
          {emptyText}
        </Typography>
      </Box>
    );
  }

  const markerDraggable = draggable && !!onPositionChange;

  const handleDragEnd = (event: google.maps.MapMouseEvent) => {
    if (!onPositionChange || !event.latLng) return;
    onPositionChange({ lat: event.latLng.lat(), lng: event.latLng.lng() });
  };

  return (
    <Box className={classes.mapContainer} style={{ height }}>
      <Map
        defaultCenter={position}
        defaultZoom={zoom ?? 17}
        mapId={mapId}
        gestureHandling="cooperative"
        disableDefaultUI={false}
        scaleControl
        style={{ width: '100%', height: '100%' }}
      >
        <MapCameraSync position={position} zoom={zoom} />
        <AdvancedMarker
          position={position}
          draggable={markerDraggable}
          onDragEnd={markerDraggable ? handleDragEnd : undefined}
        />
        {jobPosition && (
          <AdvancedMarker position={jobPosition} title="Job site">
            <Box className={classes.houseMarker}>
              <HomeIcon className={classes.houseIcon} />
            </Box>
          </AdvancedMarker>
        )}
      </Map>
    </Box>
  );
};

const MapCameraSync: FC<{ position: LatLng; zoom?: number }> = ({
  position,
  zoom,
}) => {
  const map = useMap();
  useEffect(() => {
    if (!map) return;
    map.panTo(position);
    if (zoom !== undefined) map.setZoom(zoom);
  }, [map, position.lat, position.lng, zoom]);
  return null;
};

const useStyles = makeStyles(theme => ({
  placeholder: {
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.grey[100],
    borderRadius: 8,
    padding: 16,
  },
  unavailable: {
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.grey[200],
    borderRadius: 8,
    padding: 16,
    gap: 4,
  },
  unavailableIcon: {
    marginBottom: 4,
  },
  skeleton: {
    borderRadius: 8,
  },
  mapContainer: {
    width: '100%',
    borderRadius: 8,
    overflow: 'hidden',
  },
  houseMarker: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    backgroundColor: theme.palette.background.paper,
    border: `2px solid ${theme.palette.primary.main}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: theme.shadows[2],
  },
  houseIcon: {
    color: theme.palette.primary.main,
    fontSize: 16,
  },
}));
